import StageQr from "@/components/StageQr";
import { Plus } from "lucide-react";
import EquipmentOptions from "@/components/EquipmentOptions";
import { Button } from "@/components/ui/button";
import WindingStrip from "@/components/WindingStrip";

// "4. AVT FINAL INSPECTION · Line Clearance · Inspection", the paper form PF005/F02-00, as the
// AVT stage's record. The inspection table has one row per run; the stage's quantities (which
// the waste alert and the Slitting stage read) are its totals.
const MIN_ROWS = 4;
const ROW_KEYS = ["date", "start", "finish", "issued", "rejected", "operator"];
const blankRow = () => ({ date: "", start: "", finish: "", issued: "", rejected: "", operator: "" });
const has = (v) => v !== undefined && v !== null && v !== "";
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const fmt = (n) => (Math.round(n * 1000) / 1000).toLocaleString("en-IN", { maximumFractionDigits: 3 });

// Fields of this stage the sheet covers; the rest stay available under "More details".
export const AVT_SHEET_FIELDS = new Set([
  "equipment_id", "previous_job_name", "previous_product_code", "lc_done_by", "lc_checked_by", "inspect_rows",
  "forward_meters", "done_by_production", "checked_by_qc", "winding_ticks", "remarks", "date", "start_time", "end_time",
  "operator_name", "qty_received_from_printing", "qty_inspected", "qty_forward_to_slitting", "wastage_qty",
]);

/** The inspection rows: as saved, or, for records made before the table existed, one row from the single figures. */
export function inspectRows(data) {
  if (Array.isArray(data.inspect_rows) && data.inspect_rows.length) return data.inspect_rows;
  const single = ["qty_received_from_printing", "wastage_qty", "date", "start_time", "end_time", "operator_name"];
  if (!single.some((k) => has(data[k]))) return [];
  return [{ date: data.date ?? "", start: data.start_time ?? "", finish: data.end_time ?? "",
    issued: data.qty_received_from_printing ?? "", rejected: data.wastage_qty ?? "", operator: data.operator_name ?? "" }];
}

/** Totals of the rows, and the running metres of the labels forwarded (labels x repeat length / (1000 x ups)). */
export function avtFigures(data, carried = {}) {
  const rows = inspectRows(data);
  const issued = rows.reduce((s, r) => s + num(r.issued), 0);
  const rejected = rows.reduce((s, r) => s + num(r.rejected), 0);
  const good = issued - rejected;
  const ups = num(carried.no_of_ups), length = num(carried.repeat_length_mm);
  const meters = good > 0 && ups > 0 && length > 0 ? Math.round(((good * length) / (1000 * ups)) * 10) / 10 : null;
  return { rows, issued, rejected, good, meters };
}

/** The stage fields to save for the current rows: the quantities other stages read. */
export function avtTotalsPatch(data, carried = {}) {
  const f = avtFigures(data, carried);
  if (!f.rows.length) return {};
  const first = f.rows[0];
  const finished = [...f.rows].reverse().find((r) => has(r.finish));
  const patch = {
    qty_received_from_printing: f.issued, qty_inspected: f.issued, wastage_qty: f.rejected, qty_forward_to_slitting: f.good,
    ...(has(first.date) && { date: first.date }), ...(has(first.start) && { start_time: first.start }),
    ...(finished && { end_time: finished.finish }), ...(has(first.operator) && { operator_name: first.operator }),
  };
  if (f.meters !== null) patch.forward_meters = f.meters;
  return patch;
}

const line = "border border-neutral-700";
const cell = `${line} px-1.5 py-1 text-[11px] align-middle`;
const input = "w-full bg-transparent outline-none text-[11px] font-medium placeholder:text-neutral-300 focus:bg-orange-50 disabled:text-neutral-800";
const dotted = "border-b border-dotted border-neutral-600 pb-0.5";

/** `data` is the stage record, `ctx` its context (the job, what Printing carried, the job card's winding), `setMany(patch)` changes keys. */
export default function AvtSheet({ ctx, data, setMany, readOnly }) {
  const common = ctx?.common || {};
  const carried = ctx?.carried || {};
  const f = avtFigures(data, carried);
  const shown = [...f.rows, ...Array.from({ length: Math.max(MIN_ROWS - f.rows.length, 0) }, blankRow)];
  const ticks = data.winding_ticks && typeof data.winding_ticks === "object" ? data.winding_ticks : ctx?.winding || {};

  const change = (patch) => setMany({ ...patch, ...avtTotalsPatch({ ...data, ...patch }, carried) });
  const editRow = (i, key, value) => {
    const next = shown.map((r) => ({ ...r }));
    next[i][key] = value;
    change({ inspect_rows: next.filter((r, at) => at < f.rows.length || ROW_KEYS.some((k) => has(r[k]))) });
  };

  // Render helpers, not components: a component defined here would remount on every keystroke.
  const text = (label, k, { value = data[k] ?? carried[k] ?? "", width = "" } = {}) => (
    <div key={k} className={`flex items-end gap-1.5 ${width}`}>
      <span className="text-[11px] whitespace-nowrap">{label}</span>
      <input list={k === "equipment_id" ? "equipment-ids" : undefined} className={`${input} ${dotted}`} value={value} disabled={readOnly} onChange={(e) => setMany({ [k]: e.target.value })} data-testid={`avt-${k}`} />
    </div>
  );
  const clearance = [
    ["4.1", "Check the machine & working area before going to AVT Inspection."],
    ["4.2", "Clear the previous run product before start up & check the same size & same color jobs are not taken for consecutive production. Throw the wastage into dustbin and check the area and assure that the area is free of previous job material."],
    ["4.3", "Remove the previous job related data from the system."],
  ];

  return (
    <div className="bg-white border border-neutral-700 text-neutral-900" data-testid="avt-sheet">
      <EquipmentOptions types={["AVT"]} />
      <div className="flex items-start justify-between px-3 pt-2 pb-1">
        <div>
          <div className="font-bold text-sm">MedPack India Pvt. Ltd.</div>
          <div className="print-only text-[10px] text-neutral-700">
            BPR {common.mpwo_number} · {[common.customer_name, common.product_name].filter(Boolean).join(" · ")}
          </div>
        </div>
        <div className="text-center">
          <div className="font-semibold text-[12px] border-b border-neutral-700 px-6">4. AVT FINAL INSPECTION</div>
        </div>
        <div className="flex items-start gap-2"><StageQr jobId={ctx.job_id} stage="avt" /><div className="text-[11px] text-neutral-600">Page 05 of 11</div></div>
      </div>

      <div className="px-3 pb-2 space-y-1.5">
        <div className={`relative ${line} rounded-sm pt-3 p-2 mt-2.5`}>
          <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-white px-2 text-[12px] font-bold underline whitespace-nowrap">Line Clearance</div>
          <div className="grid grid-cols-3 gap-x-4 gap-y-1.5">
            {text("Equipment I.D.:", "equipment_id")}
            <span />
            <span />
            {text("Previous Job Name:", "previous_job_name")}
            <div className="flex items-end gap-1.5">
              <span className="text-[11px] whitespace-nowrap">W/o No.:</span>
              <span className={`${dotted} flex-1 text-[11px] font-semibold font-mono-num`}>{common.mpwo_number}</span>
            </div>
            {text("Previous Product Code:", "previous_product_code")}
          </div>
        </div>

        <div className={`${line} rounded-sm`}>
          <table className="w-full border-collapse">
            <tbody>
              {clearance.map(([n, t]) => (
                <tr key={n}>
                  <td className={`${cell} w-10 text-center font-semibold`}>{n}</td>
                  <td className={cell}>{t}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="grid grid-cols-2 gap-x-6 px-1.5 pb-1.5">
            {text("Done by & Date (Production)", "lc_done_by")}
            {text("Checked by & Date (Q.C.)", "lc_checked_by")}
          </div>
        </div>

        <div className="text-center text-[12px] underline pt-1">Inspection</div>
        <table className="w-full border-collapse" data-testid="avt-rows">
          <thead>
            <tr className="align-top">
              {["Date", "S.No.", "Start Time", "Finish Time", "Issued Qty. from printing (Labels)", "Rejected Qty (Labels)", "Total Good Qty. (Labels)", "Operator Sign"].map((h) => (
                <th key={h} className={`${cell} text-left font-semibold leading-tight`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r, i) => {
              const filled = has(r.issued) || has(r.rejected);
              return (
                <tr key={i} className="h-7">
                  <td className={cell}><input type="date" className={input} value={r.date ?? ""} disabled={readOnly} onChange={(e) => editRow(i, "date", e.target.value)} data-testid={`avt-row-${i}-date`} /></td>
                  <td className={`${cell} text-center`}>{i + 1}.</td>
                  <td className={cell}><input type="time" className={input} value={r.start ?? ""} disabled={readOnly} onChange={(e) => editRow(i, "start", e.target.value)} data-testid={`avt-row-${i}-start`} /></td>
                  <td className={cell}><input type="time" className={input} value={r.finish ?? ""} disabled={readOnly} onChange={(e) => editRow(i, "finish", e.target.value)} data-testid={`avt-row-${i}-finish`} /></td>
                  <td className={cell}><input type="number" className={`${input} text-right font-mono-num`} value={r.issued ?? ""} disabled={readOnly} onChange={(e) => editRow(i, "issued", e.target.value)} data-testid={`avt-row-${i}-issued`} /></td>
                  <td className={cell}><input type="number" className={`${input} text-right font-mono-num`} value={r.rejected ?? ""} disabled={readOnly} onChange={(e) => editRow(i, "rejected", e.target.value)} data-testid={`avt-row-${i}-rejected`} /></td>
                  <td className={`${cell} text-right font-mono-num`} data-testid={`avt-row-${i}-good`}>{filled ? fmt(num(r.issued) - num(r.rejected)) : ""}</td>
                  <td className={cell}><input className={input} value={r.operator ?? ""} disabled={readOnly} onChange={(e) => editRow(i, "operator", e.target.value)} data-testid={`avt-row-${i}-operator`} /></td>
                </tr>
              );
            })}
            {f.rows.length > 1 && (
              <tr className="bg-neutral-50 font-semibold">
                <td className={cell} colSpan={4}>Total</td>
                <td className={`${cell} text-right font-mono-num`}>{fmt(f.issued)}</td>
                <td className={`${cell} text-right font-mono-num`}>{fmt(f.rejected)}</td>
                <td className={`${cell} text-right font-mono-num`}>{fmt(f.good)}</td>
                <td className={cell} />
              </tr>
            )}
          </tbody>
        </table>
        {!readOnly && (
          <Button type="button" variant="outline" size="sm" className="no-print rounded-sm h-6 gap-1 text-[11px]"
            onClick={() => change({ inspect_rows: [...shown, blankRow()] })} data-testid="avt-add-row">
            <Plus size={11} /> Add inspection row
          </Button>
        )}

        <div className="text-[11px] flex flex-wrap items-end gap-x-1.5">
          <span>Quantity forwarded to slitting department :</span>
          <b className={`${dotted} min-w-24 text-center font-mono-num`} data-testid="avt-forwarded">{f.rows.length ? fmt(f.good) : ""}</b>
          <span>Labels (R.meters</span>
          {f.meters !== null
            ? <b className={`${dotted} min-w-16 text-center font-mono-num`} data-testid="avt-meters">{fmt(f.meters)}</b>
            : <input className={`${input} ${dotted} w-20 text-center`} value={data.forward_meters ?? ""} disabled={readOnly}
                onChange={(e) => setMany({ forward_meters: e.target.value })} data-testid="avt-forward_meters" />}
          <span>)</span>
        </div>
        <div>
          <div className="text-[11px] font-bold">Remarks :</div>
          <textarea className={`${input} ${dotted} min-h-9 resize-none`} value={data.remarks ?? ""} disabled={readOnly}
            onChange={(e) => setMany({ remarks: e.target.value })} data-testid="avt-remarks" />
        </div>
        <div className="text-[11px]">Wastage quantity shall be entered in reconciliation column on page 10.</div>
        <div className="grid grid-cols-2 gap-x-6">
          {text("Done by & Date (Production)", "done_by_production")}
          {text("Checked by & Date (Q.C.)", "checked_by_qc")}
        </div>

        <div className={`${line} p-1.5`} data-testid="avt-winding">
          <div className="flex gap-3 text-[11px]">
            <span className="font-semibold">5.11</span>
            <span>Mention the Winding Direction as per the Customer Specification :</span>
          </div>
          <WindingStrip ticks={ticks} onChange={(next) => setMany({ winding_ticks: next })} readOnly={readOnly} prefix="avt" />
        </div>
      </div>
      <div className="px-3 pb-2 text-[10px] text-neutral-600">FORMAT No. PF005/F02-00</div>
    </div>
  );
}
